/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type SetStateAction, useCallback } from "react";

import { productAnalytics, useTranslation } from "utils";

import type { SoilSightPersistence } from "pages/app/scenario-analysis/hooks/useSoilSightPersistence";
import { type ProjectRefinementsState, type ProjectSettings, type ScenarioData, type ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { showSoilSightSaveFailed } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { toApiSettingsRefinements } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { getChangedProjectSettingKeys } from "pages/app/scenario-analysis/utils/soilSightTracking";

type ProjectSettingsSaveArgs = {
  settings: ScenarioSettings;
  setSettings: Dispatch<SetStateAction<ScenarioSettings>>;
  setProjectRefinements: Dispatch<SetStateAction<ProjectRefinementsState>>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  closeDrawer: () => void;
  scenarios: ScenarioData[];
  persistence: Pick<SoilSightPersistence, "saveSettingsNow" | "pruneAndPersistEmptyYears">;
  passesValidation: (scenarios: ScenarioData[]) => boolean;
  recalculateAfterSettingsSave: () => Promise<void>;
};

/** Saving the project-settings drawer: applied and saved at once (not debounced), then a run that lands on the chart. */
export const useProjectSettingsSave = ({
  settings,
  setSettings,
  setProjectRefinements,
  setIsDirty,
  closeDrawer,
  scenarios,
  persistence,
  passesValidation,
  recalculateAfterSettingsSave,
}: ProjectSettingsSaveArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { saveSettingsNow, pruneAndPersistEmptyYears } = persistence;

  return useCallback(
    async function handleProjectSettingsSave(updated: ProjectSettings, shown: ProjectSettings) {
      const nextSettings: ScenarioSettings = {
        ...settings,
        market: updated.forecastControls.marketPriceTrend,
        costs: updated.forecastControls.costDevelopment,
      };
      const nextRefinements: ProjectRefinementsState = {
        location: updated.location,
        variableCosts: updated.variableCosts,
        modelSettings: updated.modelSettings,
      };

      setSettings(nextSettings);
      setProjectRefinements(nextRefinements);
      setIsDirty(true);
      closeDrawer();

      try {
        const isSaved = await saveSettingsNow(toApiSettingsRefinements({ settings: nextSettings, projectRefinements: nextRefinements }));
        if (!isSaved) return;
        productAnalytics.track("soilsights:settings_update", { setting: "project_settings", value: getChangedProjectSettingKeys(shown, updated) });
        const pruned = await pruneAndPersistEmptyYears(scenarios);
        if (!pruned || !passesValidation(pruned)) return;
        await recalculateAfterSettingsSave();
      } catch (saveError) {
        showSoilSightSaveFailed(t, saveError);
      }
    },
    [
      closeDrawer,
      passesValidation,
      pruneAndPersistEmptyYears,
      recalculateAfterSettingsSave,
      saveSettingsNow,
      scenarios,
      setIsDirty,
      setProjectRefinements,
      setSettings,
      settings,
      t,
    ]
  );
};
